/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {AxiosInstance, AxiosResponse} from "axios";
import {RetrieveUserInformation} from "./User/user.service";
import {RouteComponentProps} from "react-router-dom";
import * as Sentry from "@sentry/react";
import {KeycloakLoginReturn} from "./api_v2/Auth/auth.service";

export const newBackendAPI = axios.create({
  baseURL: import.meta.env.VITE_NEW_BACKEND,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    //"ngrok-skip-browser-warning": true, necessário para se conectar no backend via ngrok
  },
});

export const keyCloakAPI = axios.create({
  baseURL: import.meta.env.VITE_KEYCLOAK_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
  },
});

let newBackendRequestInterceptorId: number | null = null;

export const handleRequestInterceptor = (token: string) => {
  if (token) {
    newBackendAPI.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete newBackendAPI.defaults.headers.common.Authorization;
  }

  if (newBackendRequestInterceptorId !== null) {
    return newBackendRequestInterceptorId;
  }

  newBackendRequestInterceptorId = newBackendAPI.interceptors.request.use(
    async (config) => {
      const authHeader =
        (newBackendAPI.defaults.headers.common.Authorization as
          | string
          | undefined) ?? "";

      if (authHeader) {
        config.headers = config.headers ?? {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = authHeader;
        }
      }
      return config;
    },
  );

  return newBackendRequestInterceptorId;
};

type ResponseInterceptorHandlers = {
  history: RouteComponentProps["history"];
  accessDeniedHandler: () => void;
  handle401Status: () => void;
};

const responseInterceptorState = new WeakMap<
  AxiosInstance,
  {interceptorId: number | null; handlers: ResponseInterceptorHandlers}
>();

export const handleResponseInterceptor = (
  history: RouteComponentProps["history"],
  accessDeniedHandler: () => void,
  handle401Status: () => void,
  axiosInstance = newBackendAPI,
) => {
  const existingState = responseInterceptorState.get(axiosInstance);
  const handlers = {history, accessDeniedHandler, handle401Status};

  if (existingState) {
    existingState.handlers = handlers;
    return existingState.interceptorId;
  }

  const interceptorId = axiosInstance.interceptors.response.use(
    async (response) => response,
    async (error) => {
      if (error) {
        Sentry.captureException(error);
      }

      const state = responseInterceptorState.get(axiosInstance);
      const currentHandlers = state?.handlers ?? handlers;

      if (error.response?.status === 403) {
        if (error.response?.config?.method === "get") {
          currentHandlers.history.push("/access-denied");
        } else {
          currentHandlers.accessDeniedHandler();
        }
      } else if (error.response?.status === 401) {
        currentHandlers.handle401Status();
      }
      throw error;
    },
  );

  responseInterceptorState.set(axiosInstance, {interceptorId, handlers});
  return interceptorId;
};

type RefreshHandlers = {
  getRefreshToken: () => Promise<AxiosResponse<KeycloakLoginReturn<unknown>>>;
  onSessionUpdate: (data: KeycloakLoginReturn<unknown>) => void;
  onLogout: () => void;
  onError: () => void;
};

type RefreshState = {
  isRefreshing: boolean;
  failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (err: unknown) => void;
  }>;
  interceptorId: number | null;
  handlers: RefreshHandlers;
};

const refreshTokenInterceptorState = new WeakMap<AxiosInstance, RefreshState>();

export const handleRefreshTokenInterceptor = ({
  api = newBackendAPI,
  getRefreshToken,
  onSessionUpdate,
  onLogout,
  onError,
}: {
  api?: AxiosInstance;
  getRefreshToken: () => Promise<AxiosResponse<KeycloakLoginReturn<unknown>>>;
  onSessionUpdate: (data: KeycloakLoginReturn<unknown>) => void;
  onLogout: () => void;
  onError: () => void;
}) => {
  const handlers = {getRefreshToken, onSessionUpdate, onLogout, onError};
  const existingState = refreshTokenInterceptorState.get(api);

  if (existingState) {
    existingState.handlers = handlers;
    return existingState.interceptorId;
  }

  const state: RefreshState = {
    isRefreshing: false,
    failedQueue: [],
    interceptorId: null,
    handlers,
  };

  const interceptorId = api.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!error?.config) {
        state.handlers.onLogout();
        return Promise.reject(error);
      }

      const originalRequest = error.config;
      const status = error.response?.status;

      const tokenErrorMessage = error.response?.data?.message
        ?.toLowerCase()
        ?.includes("token inválido");

      const shouldRefresh =
        status === 401 || (status === 500 && tokenErrorMessage);

      if (!shouldRefresh) {
        return Promise.reject(error);
      }

      if (originalRequest._retry) {
        state.handlers.onLogout();
        return Promise.reject(error);
      }

      if (state.isRefreshing) {
        return new Promise((resolve, reject) => {
          state.failedQueue.push({resolve, reject});
        }).then((token) => {
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api.request(originalRequest);
        });
      }

      originalRequest._retry = true;
      state.isRefreshing = true;

      try {
        const {data: newAuthData} = await state.handlers.getRefreshToken();
        state.handlers.onSessionUpdate(newAuthData);

        api.defaults.headers.common.Authorization = `Bearer ${newAuthData.access_token}`;

        state.failedQueue.forEach((p) => p.resolve(newAuthData.access_token));
        state.failedQueue = [];

        return api.request({
          ...originalRequest,
          headers: {
            ...originalRequest.headers,
            Authorization: `Bearer ${newAuthData.access_token}`,
          },
        });
      } catch (err) {
        state.handlers.onError();
        state.failedQueue.forEach((p) => p.reject(err));
        state.failedQueue = [];
        state.handlers.onLogout();
        return Promise.reject(err);
      } finally {
        state.isRefreshing = false;
      }
    },
  );

  state.interceptorId = interceptorId;
  refreshTokenInterceptorState.set(api, state);
  return interceptorId;
};

export const newDataAPI = axios.create({
  baseURL: import.meta.env.VITE_NEW_API,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

newDataAPI.interceptors.request.use(async (config) => {
  if (import.meta.env.DEV) {
    console.info("Chamadas inativas em DEV");
  }

  const token: string | undefined =
    RetrieveUserInformation()?.accessToken ?? "";

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

newDataAPI.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (error) => {
    if (error) {
      Sentry.captureException(error);
    }
    throw error;
  },
);

export const localBackendAPI = axios.create({
  baseURL: import.meta.env.VITE_LOCAL_BACKEND,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

export const buildURL = (
  query: any,
  sort: any,
  page: number | null,
  limit: number,
) => {
  let url = "";
  if (query !== null && query !== undefined && query !== false)
    url = url.concat(`?q=${query}`);
  if (sort !== null && sort !== undefined) url = url.concat(`&sort=${sort}`);
  if (page !== null && page !== undefined) url = url.concat(`&_page=${page}`);
  if (limit !== null && limit !== undefined)
    url = url.concat(query ? `&_pageSize=${limit}` : `?_pageSize=${limit}`);

  return url;
};
